import { FetchError, type SaveTeam, type Team } from 'prove-common'

export type TeamFormState = SaveTeam

export const emptyForm: TeamFormState = {
  avdelingId: '',
  seksjonId: '',
  name: ''
}

export type UnitOption = {
  value: string
  label: string
  avdelingId?: string
}

export type TeamSectionGroup = {
  id: string
  name: string
  teams: Team[]
}

export type TeamCreateFailure = {
  name: string
  message: string
}

export type AvdelingGroup = {
  id: string
  name: string
  directTeams: Team[]
  sections: TeamSectionGroup[]
}

// Check status as well as error: empty HTTP error bodies are falsy in openapi-fetch.
export function checkTeamResponse(result: {
  response: Response
  error?: unknown
}) {
  if (!result.response.ok || result.error) {
    if (result.error instanceof FetchError) throw result.error
    throw new FetchError(
      result.error instanceof Error
        ? result.error.message
        : `Forespørselen mislyktes (HTTP ${result.response.status}). Prøv igjen.`,
      result.response.status
    )
  }
}

export function getOwnerKey(owner?: {
  avdelingId?: string
  seksjonId?: string
}): string {
  if (owner?.seksjonId) return `seksjon:${owner.seksjonId}`
  if (owner?.avdelingId) return `avdeling:${owner.avdelingId}`
  return ''
}

export function getCreateSuccessMessage(successCount: number): string {
  if (successCount === 1) return 'Teamet er lagt til'
  return `${successCount} team er lagt til`
}

export function getTeamSortKey(
  team: Team,
  avdelingNameById: Map<string, string>,
  seksjonNameById: Map<string, string>
): string {
  const avdelingName = avdelingNameById.get(team.avdelingId ?? '') ?? ''
  const seksjonName = seksjonNameById.get(team.seksjonId ?? '') ?? ''
  return `${avdelingName}${seksjonName}${team.name}`
}

export function buildAvdelingGroups(
  teams: Team[],
  avdelingNameById: Map<string, string>,
  seksjonNameById: Map<string, string>,
  seksjonAvdelingById: Map<string, string>
): AvdelingGroup[] {
  const groups = teams.reduce<Map<string, AvdelingGroup>>(
    (currentGroups, team) => {
      const avdelingId =
        team.avdelingId ?? seksjonAvdelingById.get(team.seksjonId ?? '') ?? ''
      const group = currentGroups.get(avdelingId) ?? {
        id: avdelingId,
        name: avdelingNameById.get(avdelingId) ?? 'Ukjent avdeling',
        directTeams: [],
        sections: []
      }

      if (team.seksjonId == null) {
        group.directTeams.push(team)
        currentGroups.set(avdelingId, group)
        return currentGroups
      }

      const existingSection = group.sections.find(
        ({ id }) => id === team.seksjonId
      )
      if (existingSection != null) {
        existingSection.teams.push(team)
        currentGroups.set(avdelingId, group)
        return currentGroups
      }

      group.sections.push({
        id: team.seksjonId,
        name: seksjonNameById.get(team.seksjonId) ?? 'Ukjent seksjon',
        teams: [team]
      })

      currentGroups.set(avdelingId, group)
      return currentGroups
    },
    new Map()
  )

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      sections: group.sections.toSorted((a, b) =>
        a.name.localeCompare(b.name, 'nb')
      )
    }))
    .toSorted((a, b) => a.name.localeCompare(b.name, 'nb'))
}

export function sortTeams(
  teams: Team[],
  avdelingNameById: Map<string, string>,
  seksjonNameById: Map<string, string>
): Team[] {
  return teams.toSorted((a, b) => {
    const leftKey = getTeamSortKey(a, avdelingNameById, seksjonNameById)
    const rightKey = getTeamSortKey(b, avdelingNameById, seksjonNameById)
    return leftKey.localeCompare(rightKey, 'nb')
  })
}

export function filterTeams(
  teams: Team[],
  searchText: string,
  avdelingNameById: Map<string, string>,
  seksjonNameById: Map<string, string>,
  seksjonAvdelingById: Map<string, string>
): Team[] {
  const normalizedSearchText = searchText.trim().toLocaleLowerCase('nb')
  if (!normalizedSearchText) return teams

  return teams.filter((team) => {
    const avdelingId =
      team.avdelingId ?? seksjonAvdelingById.get(team.seksjonId ?? '') ?? ''
    const teamName = team.name.toLocaleLowerCase('nb')
    const avdelingName = (
      avdelingNameById.get(avdelingId) ?? ''
    ).toLocaleLowerCase('nb')
    const seksjonName = (
      seksjonNameById.get(team.seksjonId ?? '') ?? ''
    ).toLocaleLowerCase('nb')

    return (
      teamName.includes(normalizedSearchText) ||
      avdelingName.includes(normalizedSearchText) ||
      seksjonName.includes(normalizedSearchText)
    )
  })
}

export function parseTeamNames(text: string): string[] {
  return text
    .split('\n')
    .map((name) => name.trim())
    .filter((name) => name.length > 0)
}

export function findDuplicateTeamNames(names: string[]): string[] {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const name of names) {
    const normalized = name.toLocaleLowerCase('nb')
    if (seen.has(normalized)) duplicates.add(normalized)
    else seen.add(normalized)
  }
  return names.filter((name, index) => {
    const normalized = name.toLocaleLowerCase('nb')
    return (
      duplicates.has(normalized) &&
      names
        .slice(0, index)
        .every((previous) => previous.toLocaleLowerCase('nb') !== normalized)
    )
  })
}

export function findExistingTeamDuplicates(
  names: string[],
  existingTeams: Team[],
  owner?: { avdelingId?: string; seksjonId?: string }
): string[] {
  const ownerKey = getOwnerKey(owner)
  if (!ownerKey) return []

  const existingNameSet = new Set(
    existingTeams
      .filter((team) => getOwnerKey(team) === ownerKey)
      .map((team) => team.name.trim().toLocaleLowerCase('nb'))
  )

  const reported = new Set<string>()
  return names.filter((name) => {
    const normalized = name.toLocaleLowerCase('nb')
    if (!existingNameSet.has(normalized) || reported.has(normalized))
      return false
    reported.add(normalized)
    return true
  })
}
